import { Button, Column, Control, Field, Help, Icon, Input, Label, Table, Title } from 'rbx';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { friendlyRole } from '../../../models/IMembershipModel';
import Routing from '../../../Routing';
import { isRequired } from '../../../utils/helpers';
import { t } from '../../../utils/translate';
import IsAdminQuery from '../../auth/IsAdminQuery';
import NavButton from '../../nav/NavButton';
import UsersForPlantQueryContainer from '../queries/UsersForPlantQuery';
import {
    IPlantAddressModel,
    IPlantUpdateModel,
    IPlantUserRoleModel,
} from './../../../models/IPlantModel';
import GroupListSelectMenu from './../../group/GroupListSelectMenu';
import PlantAddressForm from './PlantAddressForm';

interface IProps {
    onUpdate: (plant: IPlantUpdateModel) => void;
    plant: IPlantUpdateModel;
}

export default class PlantUpdateForm extends React.Component<IProps, any> {
    private nameChange = (context: any): void => {
        this.props.onUpdate({
            ...this.props.plant,
            name: context.currentTarget.value,
        });
    };

    private customerDisplayNameChange = (context: any): void => {
        this.props.onUpdate({
            ...this.props.plant,
            customerDisplayName: context.currentTarget.value,
        });
    };

    private deliveryAddressChange = (address: IPlantAddressModel): void => {
        this.props.onUpdate({
            ...this.props.plant,
            deliveryAddress: address,
        });
    };

    private invoiceAddressChange = (address: IPlantAddressModel): void => {
        this.props.onUpdate({
            ...this.props.plant,
            invoiceAddress: address,
        });
    };

    private groupsChange = (groupIds: number[]): void => {
        this.props.onUpdate({
            ...this.props.plant,
            groupIds,
        });
    };

    private renderUser = (iPlantUserRoleModel: IPlantUserRoleModel): JSX.Element => {
        const { user, role } = iPlantUserRoleModel;

        return (
            user && (
                <tr key={user.id}>
                    <td>{user.name}</td>
                    <td className="ellipsis" style={{ maxWidth: '15vw' }}>
                        {user.email}
                    </td>
                    <td>{friendlyRole(role)}</td>
                    <td>
                        <NavButton to={Routing.USER_DETAILS.getPath(`${user.id}`)} size="small">
                            View
                        </NavButton>
                    </td>
                </tr>
            )
        );
    };

    private renderUsers = (iPlantUserRoleModels: IPlantUserRoleModel[]): JSX.Element => {
        if (!iPlantUserRoleModels || !iPlantUserRoleModels.length) {
            return <p>No users found</p>;
        }
        return (
            <table className="table is-fullwidth">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th />
                    </tr>
                </thead>
                <tbody>{iPlantUserRoleModels.map(this.renderUser)}</tbody>
            </table>
        );
    };

    private renderAdminActionButtons = () => {
        const { plant } = this.props;
        return (
            <>
                {plant && (
                    <>
                        <Link
                            className="button is-primary"
                            to={Routing.PLANT_ONBOARDING.getPath(plant.id)}
                        >
                            Onboarding
                        </Link>
                        <Link
                            className="button is-primary"
                            to={Routing.PLANT_TAGS.getPath(plant.id)}
                        >
                            View Tags
                        </Link>
                    </>
                )}
            </>
        );
    };

    public render = () => {
        const {
            name,
            id,
            groupIds,
            s3Folder,
            customerDisplayName,
            invoiceAddress,
            deliveryAddress,
        } = this.props.plant;
        const isNameNotValid = name!.length < 3;
        return (
            <>
                <Field horizontal={true}>
                    <div className="field-label is-normal">
                        <Label>S3 Folder</Label>
                    </div>
                    <Field.Body>
                        <Field>
                            <Control expanded={true} iconLeft={true} iconRight={false}>
                                <Input
                                    as="input"
                                    type="text"
                                    placeholder="S3 folder"
                                    value={`plants/${s3Folder}`}
                                    disabled={true}
                                />
                                <Icon size="small" align="left">
                                    <i className="fa fa-folder-tree" />
                                </Icon>
                            </Control>
                        </Field>
                    </Field.Body>
                </Field>

                <Field horizontal={true}>
                    <div className="field-label is-normal">
                        <Label>LE Plant ID</Label>
                    </div>
                    <Field.Body>
                        <Field>
                            <Control expanded={true} iconLeft={true} iconRight={false}>
                                <Input
                                    as="input"
                                    type="text"
                                    placeholder="LE Plant ID"
                                    value={id}
                                    disabled={true}
                                />
                                <Icon size="small" align="left">
                                    <i className="fa fa-building" />
                                </Icon>
                            </Control>
                        </Field>
                    </Field.Body>
                </Field>

                <Field horizontal={true}>
                    <div className="field-label is-normal">
                        <Label>{isRequired('Plant name')}</Label>
                    </div>
                    <Field.Body>
                        <Field>
                            <Control expanded={true} iconLeft={true}>
                                <Input
                                    as="input"
                                    type="text"
                                    placeholder="Name"
                                    value={name}
                                    color={undefined}
                                    onChange={this.nameChange}
                                />
                                <Icon size="small" align="left">
                                    <i className="fa fa-building" />
                                </Icon>
                                {isNameNotValid && (
                                    <Help color="danger">{t().plant_name_id_complexity}</Help>
                                )}
                            </Control>
                        </Field>
                    </Field.Body>
                </Field>

                <Field horizontal={true}>
                    <div className="field-label is-normal">
                        <Label>Customer Display Name</Label>
                    </div>
                    <Field.Body>
                        <Field>
                            <Control expanded={true} iconLeft={true}>
                                <Input
                                    as="input"
                                    type="text"
                                    placeholder="Name"
                                    value={customerDisplayName || ''}
                                    onChange={this.customerDisplayNameChange}
                                />
                                <Icon size="small" align="left">
                                    <i className="fa fa-building" />
                                </Icon>
                            </Control>
                        </Field>
                    </Field.Body>
                </Field>

                <Field horizontal={true}>
                    <div className="field-label is-normal">
                        <Label>Assign to group</Label>
                    </div>
                    <Field.Body>
                        <Field>
                            <GroupListSelectMenu
                                multiple={true}
                                onUpdate={this.groupsChange}
                                defaultSelected={groupIds}
                            />
                        </Field>
                    </Field.Body>
                </Field>

                <hr />

                <Button.Group>
                    <IsAdminQuery renderAdmin={this.renderAdminActionButtons} />

                    <Link className="button is-primary" to={Routing.SEARCH_PLANT.getPath(id)}>
                        Search
                    </Link>
                </Button.Group>

                <hr />

                <div className="columns">
                    <div className="column">
                        <PlantAddressForm
                            hideLatLng={true}
                            title="Invoice Address"
                            address={invoiceAddress || {}}
                            onUpdate={this.invoiceAddressChange}
                        />
                    </div>
                    <div className="column">
                        <PlantAddressForm
                            title="Delivery Address"
                            address={deliveryAddress || {}}
                            onUpdate={this.deliveryAddressChange}
                        />
                    </div>
                </div>

                <hr />

                <Title size={5}>
                    Users
                    <NavButton to={Routing.USER_LIST.route} pull="right">
                        Add Users
                    </NavButton>
                </Title>
                <UsersForPlantQueryContainer plantId={id} renderFetchedUsers={this.renderUsers} />
            </>
        );
    };
}
